/**
 * Platform fee taken on payments processed through the gateway on behalf of a
 * banker. Cash collected on routes never goes through here.
 */

import { toMinorSafe } from './billingMoney';

/** 3% expressed in basis points. */
export const PLATFORM_FEE_BPS = 300;

export const BPS_DIVISOR = 10_000;

export function isValidFeeBps(bps: unknown): boolean {
  const num = Number(bps);
  return Number.isFinite(num) && num >= 0 && num <= BPS_DIVISOR;
}

/** Fee in cents, rounded half-up, never above the charge itself. */
export function computePlatformFee(
  amountMinor: number,
  bps: number = PLATFORM_FEE_BPS,
): number {
  const amount = Math.max(0, toMinorSafe(amountMinor));
  const rate = isValidFeeBps(bps) ? Number(bps) : PLATFORM_FEE_BPS;
  return Math.min(amount, Math.round((amount * rate) / BPS_DIVISOR));
}

/** What the banker receives after the platform fee. */
export function netAfterPlatformFee(
  amountMinor: number,
  bps: number = PLATFORM_FEE_BPS,
): number {
  const amount = Math.max(0, toMinorSafe(amountMinor));
  return amount - computePlatformFee(amount, bps);
}

/**
 * Fee to reverse when a charge is refunded. Partial refunds return the same
 * proportion of the fee, so the platform never keeps a fee for money returned.
 */
export function refundedPlatformFee(
  chargeMinor: number,
  refundMinor: number,
  feeMinor: number,
): number {
  const charge = Math.max(0, toMinorSafe(chargeMinor));
  const refund = Math.min(charge, Math.max(0, toMinorSafe(refundMinor)));
  const fee = Math.max(0, toMinorSafe(feeMinor));
  if (charge === 0 || refund === 0 || fee === 0) return 0;
  if (refund === charge) return fee;
  return Math.min(fee, Math.round((fee * refund) / charge));
}

export function feePercentLabel(bps: number = PLATFORM_FEE_BPS): string {
  const percent = (isValidFeeBps(bps) ? Number(bps) : PLATFORM_FEE_BPS) / 100;
  return `${Number.isInteger(percent) ? percent : percent.toFixed(2)}%`;
}
